/**
 * Fiscal → Pendências fiscais (v7.1): divergências e dados a confirmar (ex.: com o contador)
 * de uma regra tributária ou de um produto. Enquanto abertas, impedem a aprovação para
 * produção e a emissão real — não impedem testar no simulador. Resolver é de quem aprova
 * regras, sempre com a justificativa (quem confirmou, quando e o quê).
 */
import { FISCAL_PENDENCY_SOURCE_LABEL, type FiscalPendencyView } from "@cafegestor/shared";
import { useState } from "react";
import { useCan } from "@/app/me";
import { Alert, Badge, Button, Card, PageTitle, Select, Spinner } from "@/components/ui";
import { SaveError } from "../cadastros/widgets";
import { dateTimeBR } from "../estoque/lib";
import { useFiscalAction, useFiscalQuery } from "./lib";

export function PendenciasFiscaisPage() {
  const [status, setStatus] = useState<"aberta" | "resolvida" | "todas">("aberta");
  const list = useFiscalQuery<FiscalPendencyView[]>(`/pendencias?status=${status}`);
  return (
    <div className="space-y-6">
      <PageTitle
        title="Pendências fiscais"
        description="Divergências e dados a confirmar com o contador. Enquanto abertas, a regra não é aprovada para produção e a nota com valor fiscal não é emitida; o teste no simulador continua liberado."
      />
      <Card>
        <div className="mb-4 w-64">
          <Select
            aria-label="Situação das pendências"
            value={status}
            onChange={(e) => setStatus(e.target.value as typeof status)}
          >
            <option value="aberta">Abertas</option>
            <option value="resolvida">Resolvidas</option>
            <option value="todas">Todas</option>
          </Select>
        </div>
        {list.isPending ? (
          <Spinner />
        ) : (list.data?.length ?? 0) === 0 ? (
          <p className="text-sm text-muted-foreground">
            {status === "aberta" ? "Nenhuma pendência aberta." : "Nenhuma pendência encontrada."}
          </p>
        ) : (
          <PendencyList items={list.data ?? []} showSubject />
        )}
      </Card>
    </div>
  );
}

/** Lista de pendências com a resolução (quem pode aprovar regras). Usada também na regra. */
export function PendencyList({
  items,
  showSubject = false,
}: {
  items: FiscalPendencyView[];
  showSubject?: boolean;
}) {
  return (
    <ul className="space-y-3" aria-label="Pendências fiscais">
      {items.map((p) => (
        <PendencyItem key={p.id} p={p} showSubject={showSubject} />
      ))}
    </ul>
  );
}

function PendencyItem({ p, showSubject }: { p: FiscalPendencyView; showSubject: boolean }) {
  const can = useCan();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const resolve = useFiscalAction<{ resolution: string; version: number }>(
    () => `/pendencias/${p.id}/resolver`,
    "POST",
    () => setOpen(false),
    "Pendência resolvida.",
  );
  const subject = p.ruleCode
    ? `Regra ${p.ruleCode}${p.ruleName ? ` — ${p.ruleName}` : ""}`
    : `Produto ${p.productCode ?? ""}${p.productName ? ` — ${p.productName}` : ""}`;
  return (
    <li className="rounded-md border p-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={p.status === "aberta" ? "warn" : "ok"}>
          {p.status === "aberta" ? "Aberta — impede produção" : "Resolvida"}
        </Badge>
        <strong>{p.title}</strong>
        <span className="font-mono text-xs text-muted-foreground">{p.code}</span>
      </div>
      {showSubject && <p className="mt-1 text-xs text-muted-foreground">{subject}</p>}
      <p className="mt-1 whitespace-pre-line">{p.description}</p>
      <p className="mt-1 text-xs text-muted-foreground">
        {FISCAL_PENDENCY_SOURCE_LABEL[p.source]} · aberta por {p.openedByEmail ?? "—"} em{" "}
        {dateTimeBR(p.openedAt)}
      </p>
      {p.status === "resolvida" && (
        <p className="mt-1 text-xs">
          Resolvida por {p.resolvedByEmail ?? "—"} em {dateTimeBR(p.resolvedAt)}: {p.resolution}
        </p>
      )}
      {p.status === "aberta" && can("fiscal.matriz.aprovar") && (
        <div className="mt-2">
          {!open ? (
            <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>
              Resolver
            </Button>
          ) : (
            <div className="space-y-2">
              <label className="block text-sm">
                <span className="font-medium">Resolução</span>
                <textarea
                  className="mt-1 block w-full rounded-md border bg-background px-3 py-2 text-sm"
                  rows={3}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Ex.: Contador Fulano (CRC GO-000000) confirmou o NCM 0901.21.00 em 13/10/2026; cadastro corrigido."
                />
              </label>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  disabled={resolve.isPending}
                  onClick={() => {
                    if (text.trim().length < 10) {
                      setFormError(
                        "Descreva a resolução: quem confirmou, quando e o que foi decidido.",
                      );
                      return;
                    }
                    setFormError(null);
                    resolve.mutate({ resolution: text.trim(), version: p.version });
                  }}
                >
                  Registrar resolução
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
                  Cancelar
                </Button>
              </div>
              {formError && <Alert tone="error">{formError}</Alert>}
              <SaveError error={resolve.error} />
            </div>
          )}
        </div>
      )}
    </li>
  );
}
